/**
 * Registers the Ghost `<popular-rvs>` custom element so CMS HTML can mount
 * the Vue listing after `v-html` inserts the tag.
 */
import type { VNode } from 'vue'
import { h, render, Suspense } from 'vue'

export default function usePopularRvsElement() {
  const nuxtApp = useNuxtApp()

  // Defining the element upgrades matching nodes that are already in the DOM.
  // On a server rendered page those are the SSR nodes, which hydration throws
  // away when it rewrites the `v-html` content of GhostBody, so registering
  // before then mounts the listing twice and refetches unified-search.
  onMounted(() => {
    if (customElements.get('popular-rvs')) {
      return
    }

    class PopularRvsElement extends HTMLElement {
      vnode?: VNode

      connectedCallback() {
        this.style.display = 'block'
        this.classList.add('not-prose')
        void this.mount()
      }

      async mount() {
        const { default: PopularRvs } = await import('./popular-rvs.vue')

        if (!this.isConnected || this.vnode) {
          return
        }

        const query = this.getAttribute('query') ?? undefined

        // Suspense mounts its content with the parentComponent it was given,
        // which is null for a root-level render(). That content vnode needs
        // its own appContext, since it won't inherit the Suspense vnode's.
        const withAppContext = (vnode: VNode) => {
          vnode.appContext = nuxtApp.vueApp._context
          return vnode
        }

        const vnode = withAppContext(h(Suspense, null, {
          default: () => withAppContext(h(PopularRvs, { query })),
        }))

        this.vnode = vnode
        nuxtApp.runWithContext(() => render(vnode, this))
      }

      disconnectedCallback() {
        render(null, this)
        this.vnode = undefined
      }
    }

    customElements.define('popular-rvs', PopularRvsElement)
  })
}
